// Apolo IDE — BrainBrowse (Sprint v2 Epic B6 WS B6.2).
//
// Lists vault notes grouped by folder. Uses obsidian_list_notes MCP tool.
// Folders are top-level only; click a note title to open in Obsidian.

import { useMemo } from 'react';
import { Folder, Loader2 } from 'lucide-react';
import { useBrainMCP } from '../../../../hooks/useBrainMCP.js';

export default function BrainBrowse() {
  const { data, loading, error } = useBrainMCP('obsidian_list_notes', { limit: 200 });
  const grouped = useMemo(() => groupByFolder(data?.notes || data?.results || []), [data]);

  function openNote(path) {
    if (typeof window !== 'undefined' && window.apolo?.openInObsidian) {
      window.apolo.openInObsidian(path).catch(() => {});
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-3 text-sm text-apolo-textTertiary" role="status">
        <Loader2 size={12} className="animate-spin" aria-hidden="true" />
        <span>Carregando vault…</span>
      </div>
    );
  }
  if (error) {
    return (
      <div role="alert" className="m-3 rounded-md border border-apolo-error/30 bg-apolo-error/10 px-2 py-1 text-xs text-apolo-error">
        {error}
      </div>
    );
  }
  if (!grouped.length) {
    return <p className="p-3 text-sm text-apolo-textTertiary">Vault vazio.</p>;
  }

  return (
    <div className="flex flex-col gap-2 p-3">
      {grouped.map(({ folder, notes }) => (
        <details key={folder} className="rounded-md border border-apolo-borderSubtle bg-apolo-surface1">
          <summary className="flex cursor-pointer items-center gap-1.5 px-2 py-1 text-xs text-apolo-textSecondary">
            <Folder size={11} aria-hidden="true" />
            <span>{folder || '(root)'}</span>
            <span className="ml-auto text-apolo-textTertiary">{notes.length}</span>
          </summary>
          <ul className="flex flex-col gap-0.5 border-t border-apolo-borderSubtle px-2 py-1">
            {notes.map((n) => {
              const title = n.title || n.name || n.path?.split('/').pop() || 'Sem título';
              return (
                <li key={n.path || title}>
                  <button
                    type="button"
                    onClick={() => openNote(n.path)}
                    className="block w-full truncate rounded px-1 py-0.5 text-left text-xs text-apolo-textPrimary transition hover:bg-apolo-surface2 focus:outline-none focus:ring-2 focus:ring-apolo-accent/50"
                    aria-label={`Abrir ${title} no Obsidian`}
                  >
                    {title}
                  </button>
                </li>
              );
            })}
          </ul>
        </details>
      ))}
    </div>
  );
}

function groupByFolder(notes) {
  const map = new Map();
  for (const n of notes || []) {
    const folder = (n.folder || n.path?.split('/').slice(0, -1).join('/') || '').replace(/^\/+/, '');
    if (!map.has(folder)) map.set(folder, []);
    map.get(folder).push(n);
  }
  return Array.from(map.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([folder, notes]) => ({ folder, notes }));
}
